<template>
  <v-card color="grey lighten-4 mb-2">
    <v-card-text class="white pa-0">
      <div class="grid-container" id="rms-strategy-table">
        <table v-if="group_of_columns">
          <thead>
            <tr class="group-row">
              <th
                v-for="group in group_names"
                :key="group"
                :colspan="group_of_columns[group].length"
                :class="[
                  {
                    'sticky-columns-shadow': group === last_sticky_group_column
                  },
                  {
                    'br-bolder': group !== last_sticky_group_column
                  }
                ]"
              >
                <template v-if="group">
                  <div class="column-group">
                    <div
                      class="d-flex justify-center align-center"
                      style="height: 20px; gap: 5px;"
                    >
                      <v-tooltip
                        bottom
                        v-if="group_of_columns[group].length === 1"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon v-bind="attrs" v-on="on" color="white" small>
                            {{ getIconGroup(group) }}
                          </v-icon>
                        </template>
                        <span>
                          <template
                            v-if="
                              group.includes('pickup_') && group !== 'pickup_0'
                            "
                          >
                            Pickup
                            {{ current_hotel.settings.pickup_spots[group] }}
                          </template>
                          <template v-else>
                            {{ $t(group) }}
                          </template>
                        </span>
                      </v-tooltip>
                      <template v-else>
                        <v-icon color="white" small>
                          {{ getIconGroup(group) }}
                        </v-icon>
                        <span style="line-height: 1;">
                          <template
                            v-if="
                              group.includes('pickup_') && group !== 'pickup_0'
                            "
                          >
                            Pickup
                            {{ current_hotel.settings.pickup_spots[group] }}
                          </template>
                          <template v-else>
                            {{ $t(group) }}
                          </template>
                        </span>
                        <template v-if="group === 'rates'">
                          <span v-show="showBulkUploadButton">
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on }">
                                <v-btn
                                  @click="show_bulk_upload_dialog = true"
                                  v-on="on"
                                  x-small
                                  icon
                                  color="white"
                                >
                                  <v-icon>mdi-upload-outline</v-icon>
                                </v-btn>
                              </template>
                              {{ $t("bulk_upload") }}
                            </v-tooltip>
                          </span>
                          <a-rms-strategy-bulk-edit
                            v-if="has_permission('rms_rates_bulk_upload')"
                            class="d-inline-block"
                          >
                            <template v-slot:button="{ open }">
                              <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                  <v-btn
                                    @click="open"
                                    v-on="on"
                                    x-small
                                    icon
                                    color="white"
                                  >
                                    <v-icon>mdi-flash-outline</v-icon>
                                  </v-btn>
                                </template>
                                {{ $t("bulk_edit") }}
                              </v-tooltip>
                            </template>
                          </a-rms-strategy-bulk-edit>
                          <span
                            v-show="showBulkUploadProgress"
                            style="width:100px;"
                          >
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on }">
                                <div
                                  class="bulk-upload-progress mt-0 ml-3"
                                  v-on="on"
                                >
                                  <v-progress-linear
                                    :value="
                                      bulkPvpMlosStatus
                                        ? bulkPvpMlosStatus.percent
                                        : 0
                                    "
                                    color="secondary lighten-2"
                                    background-color="white"
                                  ></v-progress-linear>
                                </div>
                              </template>
                              {{ $t("bulk_upload_running") }}
                            </v-tooltip>
                          </span>
                        </template>
                      </template>
                    </div>
                  </div>
                </template>
              </th>
            </tr>
            <tr class="header-row">
              <th
                v-for="column_header in column_headers"
                :key="`${column_header.group}-${column_header.column}`"
                :class="[
                  {
                    'sticky-columns-shadow':
                      column_header.column === last_sticky_group_column
                  },
                  {
                    'br-bolder':
                      last_column_of_each_group[column_header.group] ===
                        column_header.column &&
                      column_header.group !== last_sticky_group_column
                  }
                ]"
                :style="{
                  width: column_header.column == 'date' ? '200px' : '80px'
                }"
              >
                <div
                  class="cell"
                  :class="{
                    'kpi-row': !sticky_columns.includes(column_header.column),
                    'kpi-hovered':
                      kpi_hover.column === column_header.column &&
                      kpi_hover.group === column_header.group
                  }"
                  :style="{
                    width: column_header.column == 'date' ? '200px' : '80px'
                  }"
                >
                  <span
                    class="d-block day-name grey--text text--darken-3 text-uppercase"
                  >
                    <template v-if="column_header.group === 'segment'">
                      {{
                        segments.find(
                          segment => segment.code === column_header.column
                        ).name
                      }}
                    </template>
                    <template v-else-if="column_header.group === 'roomtype'">
                      <div class="text-truncate" style="width: 80px">
                        {{
                          roomTypes.find(
                            roomType => roomType.code === column_header.column
                          ).name
                        }}
                      </div>
                      <v-chip x-small>
                        {{
                          roomTypes.find(
                            roomType => roomType.code === column_header.column
                          ).available_rooms
                        }}
                      </v-chip>
                    </template>
                    <template v-else-if="column_header.group === 'board'">
                      {{
                        boards.find(
                          board => board.code === column_header.column
                        ).name
                      }}
                    </template>
                    <template
                      v-else-if="
                        column_header.group === 'rates' &&
                          column_header.column === 'rate_change'
                      "
                    >
                      <div class="d-flex align-center justify-center">
                        {{ $t(column_header.column) }}
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              icon
                              x-small
                              color="secondary"
                              class="ml-1"
                              :loading="loading_bulk_apply_prices"
                              :disabled="
                                applyChangesList.length === 0 &&
                                  applySuggestionsList.length === 0
                              "
                              @click="bulk_apply_prices_dialog = true"
                            >
                              <v-icon class="pb-1">mdi-send-outline</v-icon>
                            </v-btn>
                          </template>
                          <span>{{ $t("apply_all_rate_changes") }}</span>
                        </v-tooltip>
                      </div>
                      <v-dialog
                        v-model="bulk_apply_prices_dialog"
                        :fullscreen="$vuetify.breakpoint.xsOnly"
                        max-width="400"
                        scrollable
                      >
                        <v-card>
                          <v-card-title center class="headline justify-center">
                            {{ $t("bulk_apply_prices_title") }}
                          </v-card-title>
                          <v-card-text>
                            <div class="text-body-1 text-center mb-4">
                              {{ $t("bulk_apply_prices_text") }}
                            </div>
                            <div
                              v-if="
                                applyChangesList.length > 0 ||
                                  applySuggestionsList.length > 0
                              "
                            >
                              <div class="text-subtitle-2 mb-2">
                                {{ $t("Prices") }}:
                              </div>
                              <ul class="text-body-1">
                                <li
                                  v-for="item in applyChangesList"
                                  :key="`price-${item.date}`"
                                >
                                  <span> {{ formatDate(item.date) }}</span>
                                  <span class="mr-1">:</span>
                                  <span class="text-decoration-line-through">
                                    {{ formatPrice(item.price_current) }}
                                  </span>
                                  <span class="ml-1 mr-1">
                                    <v-icon small>
                                      mdi-arrow-right-bold
                                    </v-icon>
                                  </span>
                                  <b>{{ formatPrice(item.price) }}</b>
                                </li>
                                <li
                                  v-for="item in applySuggestionsList"
                                  :key="`suggestion-${item.date}`"
                                >
                                  <span> {{ formatDate(item.date) }}</span>
                                  <span class="mr-1">:</span>
                                  <span class="text-decoration-line-through">
                                    {{ formatPrice(item.price_current) }}
                                  </span>
                                  <span class="ml-1 mr-1">
                                    <v-icon small>
                                      mdi-arrow-right-bold
                                    </v-icon>
                                  </span>
                                  <b>{{ formatPrice(item.price) }}</b>
                                </li>
                              </ul>
                            </div>
                          </v-card-text>
                          <v-card-actions class="justify-center pb-4">
                            <v-btn
                              color="secondary"
                              rounded
                              depressed
                              class="pl-5 pr-5"
                              @click="bulk_apply_prices()"
                            >
                              {{ $t("apply") }}
                            </v-btn>
                            <v-btn
                              text
                              rounded
                              depressed
                              class="pl-5 pr-5"
                              @click="bulk_apply_prices_dialog = false"
                            >
                              {{ $t("cancel") }}
                            </v-btn>
                          </v-card-actions>
                        </v-card>
                      </v-dialog>
                    </template>
                    <template
                      v-else-if="
                        ['otb', 'stly', 'stlyn', 'ly', 'lyn', 'bud'].includes(
                          column_header.column
                        )
                      "
                    >
                      {{ column_header.column }}
                    </template>
                    <template v-else>
                      {{ $t(column_header.column) }}
                    </template>
                  </span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <aRmsStrategyDayTableRow
              v-for="day in days"
              :key="`${day}`"
              class="row-data"
              :date="day"
              :row="columns[day]"
              :nonStickyColumnHeaders="non_sticky_column_headers"
              :lastStickyColumn="last_sticky_column"
              :configStrategyTable="configStrategyTable"
              :lastColumnOfEachGroup="last_column_of_each_group"
              @clickOnDateCell="getDayDetails(day)"
              @hoveredKpi="kpi_hover = $event"
              @price-selection-changed="handlePriceSelectionChanged"
              @suggestion-selection-changed="handleSuggestionSelectionChanged"
              :kpiMode="kpiMode"
            />
            <aRmsStrategyDayTableRow
              class="row-data summary"
              :date="null"
              :row="totalPeriod"
              :nonStickyColumnHeaders="non_sticky_column_headers"
              :lastStickyColumn="last_sticky_column"
              :configStrategyTable="configStrategyTable"
              :lastColumnOfEachGroup="last_column_of_each_group"
              isSummary
              @hoveredKpi="kpi_hover = $event"
              :kpiMode="kpiMode"
            />
          </tbody>
        </table>
      </div>
    </v-card-text>
    <a-chm-bulk-upload-dialog
      v-if="has_permission('rms_rates_bulk_upload')"
      :show="show_bulk_upload_dialog"
      @close="closeBulkUploadDialog"
    ></a-chm-bulk-upload-dialog>
  </v-card>
</template>

<script>
import aChmBulkUploadDialog from "@/components/channel-manager/aChmBulkUploadDialog.vue";
import aRmsStrategyBulkEdit from "@/components/rms/clicktotravel/aRmsStrategyBulkEdit.vue";
import aRmsStrategyDayTableRow from "@/components/rms/clicktotravel/aRmsStrategyDayTableRow.vue";
import {
  getFriendlyDate,
  getFriendlyMonthStrategy,
  rateChangeLogSnackbarExtras,
  showError
} from "@/utils.js";
import Vue from "vue";
import { mapGetters, mapState } from "vuex";
var numeral = require("numeral");

let default_sticky_group_columns = [""];

export default {
  name: "a-rms-strategy-day-table",
  components: {
    aChmBulkUploadDialog,
    aRmsStrategyBulkEdit,
    aRmsStrategyDayTableRow
  },
  props: {
    configStrategyTable: Object,
    columns: Object,
    totalPeriod: Object,
    days: Array,
    roomTypes: Array,
    segments: Array,
    boards: Array,
    kpiMode: String
  },
  emits: ["getDayDetails"],
  data: () => ({
    table_class: "rms-strategy-day-table",
    filtered_rows: [],
    filtered_columns: [],
    kpi_hover: "",
    bulk_apply_prices_dialog: false,
    selected_prices: {},
    selected_suggestions: {},
    loading_bulk_apply_prices: false,
    show_bulk_upload_dialog: false
  }),
  methods: {
    getDayDetails(v) {
      this.$emit("getDayDetails", v);
    },
    getFriendlyMonthStrategy(date) {
      return getFriendlyMonthStrategy(date, true);
    },
    getHotelComparativeDates() {
      const startDate = Vue.moment(this.days[0]);
      const endDate = Vue.moment(this.days[this.days.length - 1]);

      this.$store
        .dispatch("getHotelComparativeDates", {
          hotel_id: this.current_hotel.id,
          year: startDate.format("YYYY"),
          from_date: startDate.format("YYYY-MM-DD"),
          to_date: endDate.format("YYYY-MM-DD")
        })
        .catch(() => {
          showError(this, "something_went_wrong");
        });
    },
    getIconGroup(group) {
      if (group.includes("pickup_")) {
        if (this.current_hotel.settings.pickup_spots[group] > 9) {
          return "mdi-dots-vertical-circle-outline";
        } else {
          return (
            "mdi-numeric-" +
            this.current_hotel.settings.pickup_spots[group] +
            "-circle-outline"
          );
        }
      }
      return this.configStrategyTable[group].icon;
    },
    bulk_apply_prices() {
      this.bulk_apply_prices_dialog = false;
      this.loading_bulk_apply_prices = true;

      const promises = [];

      // Apply selected prices
      if (this.applyChangesList.length > 0) {
        this.$store.dispatch("setSnackbar", {
          title: this.$t("applying_all_prices"),
          color: "secondary"
        });

        const pricePromises = this.applyChangesList.map(item => {
          const data = {
            date: item.date,
            pvp: item.price
          };
          return this.$store.dispatch("setRoomTypeDayRates", data);
        });
        promises.push(...pricePromises);
      }

      // Apply selected suggestions
      if (this.applySuggestionsList.length > 0) {
        this.$store.dispatch("setSnackbar", {
          title: this.$t("applying_price_suggestions"),
          color: "secondary"
        });

        const data = {
          hotel_id: this.current_hotel.id,
          suggestions: this.applySuggestionsList.map(item => ({
            id: item.id,
            price: item.price
          }))
        };
        promises.push(
          this.$store.dispatch("applyPricingStrategySuggestions", data)
        );
      }

      Promise.all(promises)
        .then(() => {
          this.$store.dispatch("setSnackbar", {
            title: this.$t("changes_applied_successfully"),
            color: "accent",
            ...rateChangeLogSnackbarExtras(this)
          });
          this.$store.dispatch("setReloadSubscribedModules");
          // Clear selections after successful application
          this.selected_prices = {};
          this.selected_suggestions = {};
        })
        .catch(() => {
          showError(this, "changes_not_applied");
        })
        .finally(() => {
          this.loading_bulk_apply_prices = false;
        });
    },
    handlePriceSelectionChanged(payload) {
      if (payload.selected) {
        this.$set(this.selected_prices, payload.date, {
          date: payload.date,
          price: payload.price,
          price_current: payload.price_current
        });
      } else {
        this.$delete(this.selected_prices, payload.date);
      }
    },
    handleSuggestionSelectionChanged(payload) {
      if (payload.selected) {
        this.$set(this.selected_suggestions, payload.date, {
          id: payload.suggestion.id,
          date: payload.date,
          price: payload.price,
          price_current: payload.price_current
        });
      } else {
        this.$delete(this.selected_suggestions, payload.date);
      }
    },
    formatPrice(price) {
      return (
        numeral(price).format(this.l.number0_format) + this.l.money_post_symbol
      );
    },
    formatDate(date) {
      return getFriendlyDate(date, true);
    },
    bulkImportStatus() {
      if (!this.has_permission("rms_rates_bulk_upload")) return;
      this.$store.dispatch("fetchBulkPvpMlosStatus");
    },
    closeBulkUploadDialog() {
      this.show_bulk_upload_dialog = false;
      setTimeout(() => {
        this.bulkImportStatus();
      }, 1000);
    }
  },
  computed: {
    group_names() {
      return this.configStrategyTable && Object.keys(this.configStrategyTable);
    },
    group_of_columns() {
      return (
        (this.configStrategyTable &&
          Object.keys(this.configStrategyTable).reduce((prev, curr) => {
            prev[curr] = Object.keys(this.configStrategyTable[curr].columns);
            return prev;
          }, {})) ||
        undefined
      );
    },
    column_headers() {
      const groups_and_columns = [];
      if (this.group_of_columns) {
        for (const group in this.group_of_columns) {
          this.group_of_columns[group].forEach(column => {
            let vs = null;
            if (
              group === "roomtype" ||
              group === "segment" ||
              group === "board"
            ) {
              vs = this.configStrategyTable[group].columns[column].vs;
            }
            groups_and_columns.push({ group, column, vs });
          });
        }
      }
      return groups_and_columns;
    },
    last_column_of_each_group() {
      const column_headers = [...this.column_headers].reverse();
      return column_headers.reduce((prev, curr) => {
        if (!prev[curr.group]) {
          prev[curr.group] = curr.column;
        }
        return prev;
      }, {});
    },
    non_sticky_column_headers() {
      return (
        this.column_headers &&
        this.column_headers.filter(
          column =>
            !this.sticky_columns.find(
              sticky_column =>
                column.group === sticky_column.group &&
                column.column === sticky_column.column
            )
        )
      );
    },
    sticky_group_columns() {
      return default_sticky_group_columns;
    },
    last_sticky_group_column() {
      return this.sticky_group_columns[this.sticky_group_columns.length - 1];
    },
    sticky_columns() {
      return (
        this.column_headers &&
        this.column_headers.filter(column =>
          this.group_of_columns[this.last_sticky_group_column].includes(
            column.column
          )
        )
      );
    },
    last_sticky_column() {
      return this.sticky_columns[this.sticky_columns.length - 1];
    },
    non_sticky_group_columns() {
      return this.group_columns.filter(
        group => !this.sticky_group_columns.includes(group)
      );
    },
    // returns an object of year-month: [days]
    unique_years_months() {
      return this.days.reduce((prev, curr) => {
        const year_month = curr
          .split("-")
          .slice(0, 2)
          .join("-");
        if (!prev[year_month]) {
          prev[year_month] = [];
        }
        prev[year_month].push(curr);
        return prev;
      }, {});
    },
    applyChangesList() {
      // Return array of selected prices from our reactive tracking object
      return Object.values(this.selected_prices);
    },
    applySuggestionsList() {
      // Return array of selected suggestions from our reactive tracking object
      return Object.values(this.selected_suggestions);
    },
    showBulkUploadButton() {
      return (
        this.has_permission("rms_rates_bulk_upload") &&
        this.bulkPvpMlosStatus &&
        this.bulkPvpMlosStatus.status === "done"
      );
    },
    showBulkUploadProgress() {
      return (
        this.has_permission("rms_rates_bulk_upload") &&
        this.bulkPvpMlosStatus &&
        this.bulkPvpMlosStatus.status === "running"
      );
    },
    ...mapGetters({
      current_hotel: "current_hotel",
      l: "current_hotel_language",
      has_permission: "has_permission"
    }),
    ...mapState({
      bulkPvpMlosStatus: state => state.channelmanager.bulkPvpMlosStatus
    })
  },
  mounted() {
    this.getHotelComparativeDates();
    this.bulkImportStatus();
  },
  watch: {
    current_hotel() {
      this.bulkImportStatus();
    },
    bulkPvpMlosStatus(newVal, oldVal) {
      if (!this.has_permission("rms_rates_bulk_upload")) return;
      this.$forceUpdate(); // Força una actualització del component
      if (newVal && newVal.status === "running") {
        setTimeout(() => {
          this.bulkImportStatus();
        }, 5000);
      } else if (oldVal && oldVal.status === "running") {
        this.$store.dispatch("setReloadSubscribedModules");
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.grid-container {
  overflow: auto;
  max-width: 100%;
  max-height: calc(100vh - 160px);

  .sticky-columns-shadow {
    box-shadow: 1px 0px 0px 0px rgba(0, 0, 0, 0.15),
      2px 0px 0px 0px rgba(0, 0, 0, 0.12), 4px 0px 0px 0px rgba(0, 0, 0, 0.08);
  }

  .cell {
    height: $minHeightColRow;
    border-left: $borderType $borderColorV;
    border-right: 3px solid transparent;
    border-bottom: $borderType $borderColorV;
    line-height: 16px;
    font-size: 12px;
    font-weight: 600;
    text-align: center;
    white-space: nowrap;
    min-width: 100%;
    text-wrap: wrap;
    text-transform: uppercase;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 5px;

    &.kpi-row {
      border-right: $borderType $borderColorV;
      border-bottom: 3px solid transparent;
    }

    &.kpi-hovered {
      border-bottom: 3px solid #2d6ece;
    }
  }

  table {
    border-spacing: 0;
  }

  th {
    border: none;
    border-right: $borderType $borderColorV;
    border-bottom: $borderType $borderColorV;
    /* width: 100px;
    min-width: 100px; */
    position: sticky;
    top: 0;
    background: white;
    color: #a0a0a0;
    font-weight: normal;
    height: 44px;
    z-index: 1;

    &:nth-child(1) {
      position: sticky;
      left: 0;
      background: white;
      color: #e0e0e0;
      font-weight: normal;
      z-index: 2;
    }
  }

  .header-row {
    th {
      border-right: none;
      border-bottom: none;
      top: 44px;
      box-shadow: 1px 0px 0px 0px rgba(0, 0, 0, 0.14),
        0px 1px 0px 0px rgba(0, 0, 0, 0.15), 2px 0px 0px 0px rgba(0, 0, 0, 0.11),
        0px 2px 0px 0px rgba(0, 0, 0, 0.12), 4px 0px 0px 0px rgba(0, 0, 0, 0.07),
        0px 4px 0px 0px rgba(0, 0, 0, 0.08);

      &:nth-child(1) {
        z-index: 2;
        border-bottom: $borderType $borderColorV;
      }
    }
  }

  td {
    border: none;
    /* width: 100px;
    min-width: 100px; */
    background-color: white;
    height: 44px;

    &:nth-child(1) {
      position: sticky;
      left: 0;
      background: white;
      color: #e0e0e0;
      font-weight: normal;
    }

    .cell {
      &:hover {
        background-color: #f5fdff;
      }
    }
  }

  tr.row-data {
    td.day-data {
      font-size: 0.8rem;
      font-weight: 700;
      color: #555;
      width: 200px;
      text-transform: uppercase;
      cursor: pointer;

      &::after {
        content: " ";
        position: absolute;
        top: 0;
        left: 3px;
        height: calc(100% - 8px);
        width: 4px;
        background-color: grey;
        z-index: 10;
        border-radius: 5px;
        margin: 4px 0;
      }

      &.positive::after {
        background-color: rgb(23, 171, 112);
      }

      &.negative::after {
        background-color: rgb(172, 0, 0);
      }

      &.neutral::after {
        background-color: orange;
      }
    }

    &:hover {
      td.sticky-columns-shadow {
        .cell {
          border-right: 3px solid #2d6ece;
        }
      }
    }

    &:last-child {
      position: sticky;
      bottom: 0;
      z-index: 2;
    }
  }

  .column-group {
    padding: 3px 0;
    background-color: #1576c7;
    color: white;
    border-radius: 25px;
    margin: 0 1px;
  }
}

.br-bolder {
  border-right: 2px solid #ccc !important;
}
</style>
